import { CommonModule } from '@angular/common';
import { Component, DestroyRef, forwardRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
   ControlContainer,
   ControlValueAccessor,
   FormGroup,
   FormGroupDirective,
   FormsModule,
   NG_VALUE_ACCESSOR,
   NgControl,
   ReactiveFormsModule,
} from '@angular/forms';
import { LabelInputComponent } from '@components/label-input/label-input.component';
import { ErrorMessageService } from '@core/services/error-message.service';
import { NotificationService } from '@core/services/notification.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CompanyService } from '@services/company.service';
import { TablerIconComponent } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/core/material.module';

@Component({
   selector: 'app-input-file',
   standalone: true,
   imports: [
      CommonModule,
      FormsModule,
      MaterialModule,
      ReactiveFormsModule,
      TranslateModule,
      LabelInputComponent,
      TablerIconComponent,
   ],
   providers: [CompanyService],
   viewProviders: [
      {
         provide: ControlContainer,
         useExisting: FormGroupDirective,
      },
      {
         provide: NG_VALUE_ACCESSOR,
         multi: true,
         useExisting: forwardRef(() => InputFileComponent),
      },
   ],
   templateUrl: './input-file.component.html',
})
export class InputFileComponent implements OnInit, ControlValueAccessor {
   // Variables
   form: FormGroup = new FormGroup({});
   fileName = '';

   // Services
   private parent = inject(FormGroupDirective);
   private destroyRef = inject(DestroyRef);
   public errorMessageService = inject(ErrorMessageService);
   private notificationService = inject(NotificationService);
   private translate = inject(TranslateService);
   ngControl = inject(NgControl, { optional: true, self: true })!;

   // Inputs
   readonly nameControl = input<string>();
   readonly textLabel = input<string>();
   readonly required = input<boolean>(false);
   readonly maxlength = input<any>('255');
   readonly value = input<any>();
   readonly acceptFile = input<string>('.pdf');
   readonly textTooltip = input<string>();
   readonly hasTooltip = input<boolean>(false);
   readonly placeholder = input<string>('');
   readonly disabled = input<boolean>(false);
   readonly textRequired = input<string>('');

   get acceptedFileTypes(): string[] {
      const acceptValue = (this.acceptFile() ?? '').trim();
      if (!acceptValue) return [];

      return acceptValue
         .split(',')
         .map((item) => item.trim().toLowerCase())
         .filter(Boolean)
         .map((item) => (item.startsWith('.') || item.includes('/') ? item : `.${item}`));
   }

   get acceptAttribute(): string {
      return this.acceptedFileTypes.join(',');
   }

   private isFileAccepted(file: File): boolean {
      if (!this.acceptedFileTypes.length) return true;

      const fileName = file.name.toLowerCase();
      const fileType = (file.type ?? '').toLowerCase();

      return this.acceptedFileTypes.some((allowedType) => {
         if (allowedType.startsWith('.')) return fileName.endsWith(allowedType);
         if (allowedType.endsWith('/*')) {
            const mimePrefix = allowedType.replace('/*', '/');
            return fileType.startsWith(mimePrefix);
         }
         return fileType === allowedType;
      });
   }

   private showInvalidExtensionNotification(): void {
      const acceptedTypes = this.acceptedFileTypes.join(', ');
      const message = acceptedTypes
         ? this.translate.instant('InvalidFileAllowedOnly', { acceptedTypes })
         : this.translate.instant('InvalidFile');

      this.notificationService.showDialogInfo(message, 'InvalidExtension');
   }

   get f() {
      return this.form.controls;
   }

   constructor() {
      if (this.ngControl != null) this.ngControl.valueAccessor = this;
   }

   writeValue(value: any): void {
      if (this.f[this.nameControl()!]) {
         this.f[this.nameControl()!].setValue(value);
      }
   }

   onChange: (_: any) => void = (value: any) => {
      if (this.f[this.nameControl()!]) {
         this.f[this.nameControl()!].setValue(value);
      }
   };

   onTouched: () => void = () => {};

   registerOnChange(fn: any): void {
      this.onChange = fn;
   }

   registerOnTouched(fn: any): void {
      this.onTouched = fn;
   }

   ngOnInit() {
      this.form = this.parent.form;

      const controlName = this.nameControl();
      if (controlName) {
         this.form
            .get(controlName)
            ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
               if (!value) {
                  this.fileName = '';
               }
            });
      }
   }

   selectFile(event: any): void {
      if (event.target.files && event.target.files.length > 0) {
         for (let i = 0; i < event.target.files.length; i++) {
            const file = event.target.files[i];

            if (!this.isFileAccepted(file)) {
               event.target.value = '';
               this.fileName = '';
               this.onChange(null);
               this.showInvalidExtensionNotification();
               return;
            }

            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
               this.fileName = file.name;
               this.onChange((reader.result as string).split(',')[1]);
            };
         }
      }
   }

   hasErrorMessage(): boolean {
      const control = this.form.get(this.nameControl()!);
      return !!(control?.touched && control?.invalid && control?.errors);
   }

   getErrorMessage(): string | null {
      const control = this.form.get(this.nameControl()!);
      return this.errorMessageService.getErrorMessage(
         control!,
         this.nameControl()!,
         this.textRequired()
      );
   }
}
